import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import videoApiService from "../services/api/VideoApiService";
import schedulerApiService from "../services/api/SchedulerApiService";

/**
 * Custom hook for video upload functionality
 *
 * Manages:
 * - Video listing and loading
 * - Video creation and editing
 * - Form state and validation
 * - Materials and preferences management
 * - Success/error states
 *
 * @returns {Object} Video upload state and methods
 */

export const useVideoUpload = () => {
  const { t } = useTranslation();

  // Data state
  const [videos, setVideos] = useState([]);
  const [preferencesData, setPreferencesData] = useState([]);
  const [materialsData, setMaterialsData] = useState([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Dialog and form state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Form data (keep original field names for titles/descriptions; add Vimeo URL/ID)
  const [formData, setFormData] = useState({
    videoTitleEnglish: "",
    vimeoUrlEnglish: "",
    vimeoIdEnglish: "",
    videoInstructionsEnglish: "",
    videoTitleGerman: "",
    vimeoUrlGerman: "",
    vimeoIdGerman: "",
    videoInstructionsGerman: "",
    videoPreferences: [],
    selectedMaterials: [],
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState([]);

  // Notification state
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  /** Vimeo helpers */
  const extractVimeoId = (s) => {
    const str = (s || "").trim();
    if (!str) return "";
    return (
      str.match(/vimeo\.com\/(?:video\/)?(\d{6,12})/)?.[1] ||
      str.match(/^(\d{6,12})$/)?.[1] ||
      ""
    );
  };
  const buildVimeoUrl = (id) =>
    id && /^\d{6,12}$/.test(id) ? `https://vimeo.com/${id}` : "";

  /**
   * Fetches all required data (videos, preferences, materials)
   */
  const fetchData = useCallback(async () => {
    try {
      setIsLoadingData(true);

      const [videosResponse, preferencesResponse, materialsResponse] =
        await Promise.all([
          videoApiService.getVideoList(),
          schedulerApiService.getExercisePreferences(),
          videoApiService.getVideoMaterials(),
        ]);

      setVideos(videosResponse);
      setPreferencesData(
        preferencesResponse.preferences || preferencesResponse
      );
      setMaterialsData(materialsResponse);
    } catch (error) {
      console.error("Error fetching data:", error);
      setErrorMessage(error.message || "Failed to load data");
      setShowErrorSnackbar(true);
    } finally {
      setIsLoadingData(false);
      setIsLoading(false);
    }
  }, []);

  /**
   * Handles video selection for editing
   * @param {Object} video - Video object to edit
   */
  const handleVideoEdit = (video) => {
    setSelectedVideo(video);

    // Prefer URL from backend; if only IDs exist, synthesize canonical URLs
    const idEn = extractVimeoId(
      video.urlEnglish || video.externalIdEnglish || ""
    );
    const urlEn = video.urlEnglish || buildVimeoUrl(idEn) || "";
    const idDe = extractVimeoId(video.url || video.externalId || "");
    const urlDe = video.url || buildVimeoUrl(idDe) || "";

    setFormData({
      videoTitleEnglish: video.titleEnglish || "",
      vimeoUrlEnglish: urlEn,
      vimeoIdEnglish: idEn,
      videoInstructionsEnglish: video.descriptionEnglish || "",
      videoTitleGerman: video.title || "",
      vimeoUrlGerman: urlDe,
      vimeoIdGerman: idDe,
      videoInstructionsGerman: video.description || "",
      videoPreferences: video.preferences?.map((pref) => pref.id) || [],
      selectedMaterials: video.materials?.map((mat) => mat.id) || [],
    });
    setErrors({});
    setWarnings([]);
    setOpenDialog(true);
  };

  /**
   * Handles new video creation
   */
  const handleNewVideo = () => {
    setSelectedVideo(null);
    setFormData({
      videoTitleEnglish: "",
      vimeoUrlEnglish: "",
      vimeoIdEnglish: "",
      videoInstructionsEnglish: "",
      videoTitleGerman: "",
      vimeoUrlGerman: "",
      vimeoIdGerman: "",
      videoInstructionsGerman: "",
      videoPreferences: [],
      selectedMaterials: [],
    });
    setErrors({});
    setWarnings([]);
    setOpenDialog(true);
  };

  /**
   * * Updates form field value
   * @param {string} field - Field name
   * @param {any} value - Field value
   */
  const updateFormField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // If user edits URL, derive ID
    if (field === "vimeoUrlEnglish") {
      const id = extractVimeoId(value);
      setFormData((prev) => ({ ...prev, vimeoIdEnglish: id }));
    }
    if (field === "vimeoUrlGerman") {
      const id = extractVimeoId(value);
      setFormData((prev) => ({ ...prev, vimeoIdGerman: id }));
    }

    // If user edits ID, rebuild canonical URL
    if (field === "vimeoIdEnglish") {
      const url = buildVimeoUrl(value);
      if (url) setFormData((prev) => ({ ...prev, vimeoUrlEnglish: url }));
    }
    if (field === "vimeoIdGerman") {
      const url = buildVimeoUrl(value);
      if (url) setFormData((prev) => ({ ...prev, vimeoUrlGerman: url }));
    }
  };

  /** Language completeness: either EN or DE must be fully filled */
  const isLangComplete = (lang) => {
    const title = formData[`videoTitle${lang}`]?.trim();
    const id = formData[`vimeoId${lang}`]?.trim();
    const desc = formData[`videoInstructions${lang}`]?.trim();
    return Boolean(title && id && desc);
  };

  /** Local validation (Vimeo only; no "English required") */
  const validateForm = () => {
    const english = isLangComplete("English");
    const german = isLangComplete("German");
    const errs = {};

    if (!english && !german) {
      errs._lang =
        t?.("fillAtLeastOneLanguage") ||
        "Please complete at least one language (English or German).";
    }

    // Ensure canonical Vimeo URLs (avoid backend format errors)
    const ensureCanonical = (idField, urlField, label) => {
      const id = formData[idField]?.trim();
      if (!id) return; // language omitted is fine
      const canonical = buildVimeoUrl(id);
      if (!canonical) errs[urlField] = `${label} format is invalid`;
      else if (formData[urlField] !== canonical) {
        setFormData((prev) => ({ ...prev, [urlField]: canonical }));
      }
    };
    ensureCanonical("vimeoIdEnglish", "vimeoUrlEnglish", "English video URL");
    ensureCanonical("vimeoIdGerman", "vimeoUrlGerman", "German video URL");

    // Preferences required
    if ((formData.videoPreferences?.length ?? 0) === 0) {
      errs.videoPreferences =
        t?.("selectAtLeastOnePreference") ||
        "Please select at least one preference.";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /**
   * Submits the video form
   * Partial update: omit any empty language block so existing data stays untouched.
   */
  const submitForm = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const english = isLangComplete("English");
      const german = isLangComplete("German");

      const videoData = {
        id: selectedVideo?.id || null,
        ...(english
          ? {
              urlEnglish: buildVimeoUrl(formData.vimeoIdEnglish),
              externalIdEnglish: formData.vimeoIdEnglish,
              titleEnglish: formData.videoTitleEnglish,
              descriptionEnglish: formData.videoInstructionsEnglish,
            }
          : {}),
        ...(german
          ? {
              url: buildVimeoUrl(formData.vimeoIdGerman),
              externalId: formData.vimeoIdGerman,
              title: formData.videoTitleGerman,
              description: formData.videoInstructionsGerman,
            }
          : {}),
        preferenceIds: formData.videoPreferences,
        materials: formData.selectedMaterials,
      };

      await videoApiService.saveVideo(videoData); // backend should treat missing fields as unchanged
      await fetchData();

      setShowSuccessSnackbar(true);
      setOpenDialog(false);
    } catch (error) {
      console.error("Error saving video:", error);
      setErrorMessage(error.message || "Failed to save video");
      setShowErrorSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Closes the dialog */
  const closeDialog = () => {
    setOpenDialog(false);
    setSelectedVideo(null);
    setErrors({});
    setWarnings([]);
  };

  /** Snackbars */
  const closeSuccessSnackbar = () => {
    setShowSuccessSnackbar(false);
  };

  const closeErrorSnackbar = () => {
    setShowErrorSnackbar(false);
    setErrorMessage("");
  };

  /**
   * Checks if video has missing required fields
   * @param {Object} video - Video object
   * @returns {boolean} Whether video has missing fields
   */
  const hasMissingFields = (video) => {
    return videoApiService.hasMissingRequiredFields(video);
  };

  /**
   * Gets video thumbnail URL
   * @param {Object} video - Video object
   * @returns {string} Thumbnail URL
   */
  const getVideoThumbnail = (video) => {
    const externalId = video.externalId || video.externalIdEnglish;
    return videoApiService.getVideoThumbnail(externalId);
  };

  /**
   * Deletes a video
   * @param {string} videoId - Video ID to delete
   */
  const deleteVideo = async (videoId) => {
    try {
      setIsLoading(true);
      await videoApiService.deleteVideo(videoId);
      await fetchData();
      setShowSuccessSnackbar(true);
    } catch (error) {
      console.error("Error deleting video:", error);
      setErrorMessage(error.message || "Failed to delete video");
      setShowErrorSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Duplicates a video
   * @param {string} videoId - Video ID to duplicate
   */
  const duplicateVideo = async (videoId) => {
    try {
      setIsLoading(true);
      await videoApiService.duplicateVideo(videoId, {
        titleEnglish: `${formData.videoTitleEnglish} (Copy)`,
        title: `${formData.videoTitleGerman} (Copy)`,
      });
      await fetchData();
      setShowSuccessSnackbar(true);
    } catch (error) {
      console.error("Error duplicating video:", error);
      setErrorMessage(error.message || "Failed to duplicate video");
      setShowErrorSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Computed validity for the button: either-language complete + preferences + no errors */
  const hasAnyLanguage = isLangComplete("English") || isLangComplete("German");
  const hasPrefs = (formData.videoPreferences?.length ?? 0) > 0;
  const computedIsFormValid =
    Object.keys(errors).length === 0 && hasAnyLanguage && hasPrefs;

  return {
    // Data
    videos,
    preferencesData,
    materialsData,

    // Loading states
    isLoading,
    isSubmitting,
    isLoadingData,

    // Form state
    formData,
    updateFormField,
    errors,
    warnings,

    // Dialog state
    openDialog,
    selectedVideo,
    handleVideoEdit,
    handleNewVideo,
    closeDialog,

    // Form actions
    submitForm,
    validateForm,

    // Notifications
    showSuccessSnackbar,
    showErrorSnackbar,
    errorMessage,
    closeSuccessSnackbar,
    closeErrorSnackbar,

    // Utilities
    hasMissingFields,
    getVideoThumbnail,
    deleteVideo,
    duplicateVideo,
    fetchData,

    // Computed values
    isFormValid: computedIsFormValid,
    hasWarnings: warnings.length > 0,

    // Field helpers
    getFieldProps: (fieldName) => ({
      value: formData[fieldName],
      onChange: (e) => updateFormField(fieldName, e.target.value),
      error: !!errors[fieldName],
      helperText: errors[fieldName],
    }),

    // Multi-select helpers
    getMultiSelectProps: (fieldName) => ({
      value: formData[fieldName],
      onChange: (e) => updateFormField(fieldName, e.target.value),
      error: !!errors[fieldName],
    }),
  };
};
